<template>
  <content-modal
    class="message-modal change-teacher-account-modal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    @modal-shown="actionShown"
    @modal-hidden="actionHidden"
    @modal-hide-request="actionHideRequest"
  >
    <template v-if="use_title" #title>{{ t("change_to_teacher_modal_title_label") }}</template>
    <template v-if="use_content" #content>
      <modal-content>
        <template #content>
          <h6 class="title">
            {{ t("change_to_teacher_modal_content_sls_label") }}
          </h6>
          <cv-form @submit.prevent>
            <dl v-if="showDisplayName" class="field-set type-text">
              <dt class="field-label">
                {{ t("change_to_teacher_modal_teacher_name_label") }}
              </dt>
              <dd class="field-value">
                {{ props.params?.userDisplayName }}
              </dd>
            </dl>
            <dl class="field-set type-password">
              <dt class="field-label">
                {{ t("change_to_teacher_modal_sls_password_label") }}
              </dt>
              <dd class="field-value">
                <text-input
                  :error-message="errorMessage"
                  :character-limit="characterLimit"
                  input-type="password"
                  @input="handleUpdatePassword"
                />
              </dd>
            </dl>
          </cv-form>
        </template>
        <template #tools>
          <cv-button class="cancel" kind="secondary" size="field" @click="actionSecondary">
            {{ t("button_cancel") }}
          </cv-button>
          <fetch-data-button
            kind="primary"
            size="field"
            :loading="processingState.isLoading"
            :error="processingState.error"
            :error-message="processingState.errorMessage"
            :tip-position="processingState.tipPosition"
            @click="actionPrimary"
          >
            <span v-if="processingState.error">{{ t("button_state_error") }}</span>
            <span v-else>{{ t("button_proceed") }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import modalEventBus from "@plugins/modal/service/event-bus";
import { isEmpty } from "@utils/var.check";
import profileApi from "@apis/profile.api";
import { validateMaxCharacterLimit } from "@plugins/common/utils/text-input-validation-helper.ts";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm.ts";
import { ref, computed, onMounted } from "vue";

defineOptions({
  name: "ChangeTeacherAccountModal",
});

interface Props {
  params?: {
    userDisplayName?: string;
    actionPrimary?: (data: { validated: boolean; password: string }) => void;
    actionSecondary?: (data: { validated: boolean; password: string }) => void;
  } | null;
}

const props = withDefaults(defineProps<Props>(), {
  params: null,
});

const { t } = useI18n();
const { vmNode } = useVm();

const password = ref("");
const closeAriaLabel = ref("Close");
const use_title = ref(true);
const use_content = ref(true);
const size = ref("small");
const autoHideOff = ref(true);
const visible = ref(true);
const errorMessage = ref("");
const characterLimit = ref(50);
const processingState = ref({
  isLoading: false,
  error: false,
  errorMessage: "",
  tipPosition: "top",
});

const showDisplayName = computed(() => {
  return props.params?.userDisplayName && props.params?.userDisplayName.length > 0;
});

const isProcessing = () => {
  return processingState.value.isLoading;
};

const setProcessing = () => {
  processingState.value.isLoading = true;
};

const resetProcessingState = () => {
  processingState.value.isLoading = false;
  processingState.value.error = false;
};

const setProcessingError = () => {
  processingState.value.isLoading = false;
  processingState.value.error = true;
  // short delay before removing the error state.
  setTimeout(() => {
    resetProcessingState();
  }, 3000);
};

const validate = () => {
  if (isEmpty(password.value)) {
    errorMessage.value = t("password_modal_empty_message_label");
    return false;
  } else if (!validateMaxCharacterLimit(password.value, characterLimit.value)) {
    return false;
  }
  return true;
};

const verifyPassword = () => {
  if (isProcessing()) {
    return;
  }
  setProcessing();

  if (validate()) {
    profileApi.verifyPassword(password.value).then((response) => {
      if (response.code !== "ok") {
        errorMessage.value = t("password_modal_error_message_label");
        resetProcessingState();
        return;
      }
      props.params?.actionPrimary?.({ validated: true, password: password.value });
      resetProcessingState();
      visible.value = false;
    });
  } else {
    setProcessingError();
  }
};

const handleUpdatePassword = (val: string) => {
  password.value = val;
  errorMessage.value = "";
};

const actionShown = () => {
  visible.value = true;
};

const actionHidden = () => {
  props.params?.actionSecondary?.({ validated: false, password: password.value });
  visible.value = false;
  modalEventBus.$emit("modal-hidden", vmNode.key);
};

const actionPrimary = () => {
  verifyPassword();
};

const actionSecondary = () => {
  props.params?.actionSecondary?.({ validated: false, password: password.value });
  visible.value = false;
};

const actionHideRequest = (event: CustomEvent) => {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    actionHidden();
  }
};

onMounted(() => {
  visible.value = true;
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/spacing";

.change-teacher-account-modal {
  .bx--form {
    padding-bottom: $spacing-05;
  }
}
</style>
